/* Prüfungen eines Dokuments: Pflicht-Entities, Spawns, Licht, eingeklemmte Objekte, Leak-Test und fehlende Texturen. */
import type { CheckItem, CheckLevel, Doc, Entity, MapKind, Vec3 } from './types.ts';
import I18n from '../i18n.ts';
import { dot, parseVec } from './vec.ts';
import { bbox, faceNormal } from './brush.ts';
import { analyze, FINDING_KEYS, findings } from './complexity.ts';
import { hiddenBrushes, performanceItems, SPECIAL } from './perf.ts';
import { lintItems, selOfEnts } from './lint.ts';
import type { TexSize } from './lint.ts';

const _ = I18n.t;

export { SPECIAL };
export const isSpecialTex = (t: string): boolean => SPECIAL.test(t);

interface SolidBrush { planes: { n: Vec3; d: number }[]; min: Vec3; max: Vec3 }

function solidList(doc: Doc): SolidBrush[] {
  const out: SolidBrush[] = [];
  doc.ents[0].brushes.forEach((b) => {
    if (b.faces.every((f) => /^clip$/i.test(f.tex))) return;
    const bb = bbox(b);
    out.push({ planes: b.faces.map((f) => { const n = faceNormal(b.verts, f.vi); return { n: n, d: dot(n, b.verts[f.vi[0]]) }; }), min: bb.min, max: bb.max });
  });
  return out;
}
const inBrush = (sb: SolidBrush, p: Vec3): boolean => { for (let i = 0; i < sb.planes.length; i++) if (dot(sb.planes[i].n, p) - sb.planes[i].d > 0.001) return false; return true; };
function pointInSolid(list: SolidBrush[], p: Vec3): boolean {
  for (const sb of list) {
    if (p[0] < sb.min[0] || p[1] < sb.min[1] || p[2] < sb.min[2] || p[0] > sb.max[0] || p[1] > sb.max[1] || p[2] > sb.max[2]) continue;
    if (inBrush(sb, p)) return true;
  }
  return false;
}

type LeakStatus = 'skipped' | 'outside' | 'start-solid' | 'leak' | 'sealed';
/** Grobes Raster aus den soliden Brushes, Flutung von der Spielerposition aus: erreicht sie den Rand, ist die Karte offen. */
function leakTest(list: SolidBrush[], start: Vec3): { status: LeakStatus; g?: number; isVoid?: (p: Vec3) => boolean } {
  if (!list.length) return { status: 'skipped' };
  const mn: Vec3 = [Infinity, Infinity, Infinity], mx: Vec3 = [-Infinity, -Infinity, -Infinity];
  list.forEach((b) => { for (let x = 0; x < 3; x++) { if (b.min[x] < mn[x]) mn[x] = b.min[x]; if (b.max[x] > mx[x]) mx[x] = b.max[x]; } });
  for (let x = 0; x < 3; x++) { mn[x] -= 24; mx[x] += 24; }
  const sz: Vec3 = [mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2]];
  const count = (g: number) => Math.ceil(sz[0] / g) * Math.ceil(sz[1] / g) * Math.ceil(sz[2] / g);
  let g = 8;
  while (count(g) > 4e6 && g < 64) g *= 2;
  if (count(g) > 4e6) return { status: 'skipped' };
  const nx = Math.ceil(sz[0] / g), ny = Math.ceil(sz[1] / g), nz = Math.ceil(sz[2] / g);
  const grid = new Uint8Array(nx * ny * nz);
  list.forEach((b) => {
    const i0 = Math.max(0, Math.floor((b.min[0] - mn[0]) / g)), i1 = Math.min(nx - 1, Math.floor((b.max[0] - mn[0]) / g));
    const j0 = Math.max(0, Math.floor((b.min[1] - mn[1]) / g)), j1 = Math.min(ny - 1, Math.floor((b.max[1] - mn[1]) / g));
    const k0 = Math.max(0, Math.floor((b.min[2] - mn[2]) / g)), k1 = Math.min(nz - 1, Math.floor((b.max[2] - mn[2]) / g));
    for (let k = k0; k <= k1; k++) for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      if (inBrush(b, [mn[0] + (i + 0.5) * g, mn[1] + (j + 0.5) * g, mn[2] + (k + 0.5) * g])) grid[i + nx * (j + ny * k)] = 1;
    }
  });
  const si = Math.floor((start[0] - mn[0]) / g), sj = Math.floor((start[1] - mn[1]) / g), sk = Math.floor((start[2] - mn[2]) / g);
  if (si < 0 || sj < 0 || sk < 0 || si >= nx || sj >= ny || sk >= nz) return { status: 'outside', g: g };
  const s0 = si + nx * (sj + ny * sk);
  if (grid[s0] === 1) return { status: 'start-solid', g: g };
  const stack = new Int32Array(nx * ny * nz); let ptr = 0, reached = false;
  grid[s0] = 2; stack[ptr++] = s0;
  const layer = nx * ny;
  while (ptr) {
    const c = stack[--ptr], i = c % nx, j = Math.floor(c / nx) % ny, k = Math.floor(c / layer);
    if (i === 0 || j === 0 || k === 0 || i === nx - 1 || j === ny - 1 || k === nz - 1) { reached = true; break; }
    const nb = [c - 1, c + 1, c - nx, c + nx, c - layer, c + layer];
    for (let q = 0; q < 6; q++) if (grid[nb[q]] === 0) { grid[nb[q]] = 2; stack[ptr++] = nb[q]; }
  }
  if (reached) return { status: 'leak', g: g };
  // dicht: Außenraum vom Rand aus fluten, damit sich Entities im Void finden lassen
  ptr = 0;
  for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    if (i && j && k && i < nx - 1 && j < ny - 1 && k < nz - 1) continue;
    const c0 = i + nx * (j + ny * k);
    if (grid[c0] === 0) { grid[c0] = 3; stack[ptr++] = c0; }
  }
  while (ptr) {
    const c = stack[--ptr], i = c % nx, j = Math.floor(c / nx) % ny, k = Math.floor(c / layer);
    if (i === 0 || j === 0 || k === 0 || i === nx - 1 || j === ny - 1 || k === nz - 1) {
      // Randzellen: nur Nachbarn innerhalb des Rasters
      const cand: [number, number, number][] = [[i - 1, j, k], [i + 1, j, k], [i, j - 1, k], [i, j + 1, k], [i, j, k - 1], [i, j, k + 1]];
      for (const [a, b, d] of cand) {
        if (a < 0 || b < 0 || d < 0 || a >= nx || b >= ny || d >= nz) continue;
        const q = a + nx * (b + ny * d);
        if (grid[q] === 0) { grid[q] = 3; stack[ptr++] = q; }
      }
      continue;
    }
    const nb = [c - 1, c + 1, c - nx, c + nx, c - layer, c + layer];
    for (let q = 0; q < 6; q++) if (grid[nb[q]] === 0) { grid[nb[q]] = 3; stack[ptr++] = nb[q]; }
  }
  const isVoid = (p: Vec3): boolean => {
    const a = Math.floor((p[0] - mn[0]) / g), b = Math.floor((p[1] - mn[1]) / g), d = Math.floor((p[2] - mn[2]) / g);
    if (a < 0 || b < 0 || d < 0 || a >= nx || b >= ny || d >= nz) return true; // jenseits des Rasters: sicher außerhalb
    return grid[a + nx * (b + ny * d)] === 3;
  };
  return { status: 'sealed', g: g, isVoid: isVoid };
}

/** opts.hasTex(name) -> bool, optional (nur wenn WADs geladen sind). */
export function validate(doc: Doc, opts?: { hasTex?: (name: string) => boolean; texSize?: TexSize; skipLeak?: boolean }): CheckItem[] {
  const o = opts || {};
  const out: CheckItem[] = [], add_ = (level: CheckLevel, id: string, text: string, sel?: CheckItem['sel']) => out.push(sel && (sel.b.length || sel.e.length) ? { level: level, id: id, text: text, sel: sel } : { level: level, id: id, text: text });
  const pick = (...ents: Entity[]): CheckItem['sel'] => ({ b: [], e: ents.map((e) => e.id) });
  const world = doc.ents[0];
  add_(world && world.kv.classname === 'worldspawn' ? 'ok' : 'err', 'core.c_worldspawn', _('core.c_worldspawn'));
  let total = 0; const texs: Record<string, number> = {};
  doc.ents.forEach((e) => e.brushes.forEach((b) => {
    total++;
    b.faces.forEach((f) => { const t = f.tex.toUpperCase(); texs[t] = (texs[t] || 0) + 1; });
  }));
  add_(total ? 'ok' : 'warn', total ? 'core.c_brushes' : 'core.c_no_brushes', total ? _('core.c_brushes', { count: total }) : _('core.c_no_brushes'));
  const mk = mapKind(doc.ents), starts = doc.ents.filter((e) => e.kv.classname === 'info_player_start'), dm = mk.counts.dm;
  add_('info', 'chk.map_type', _('chk.map_type', { label: mk.label, why: mk.why }));
  if (mk.kind === 'sp') {
    if (starts.length === 1) add_('ok', 'core.c_start_ok', _('core.c_start_ok'));
    else if (!starts.length) add_('err', 'core.c_start_missing', _('core.c_start_missing'));
    else add_('warn', 'core.c_start_multi', _('core.c_start_multi', { count: starts.length }), pick(...starts));
    if (!mk.counts.changelevel) add_('info', 'core.c_no_changelevel', _('core.c_no_changelevel'));
  } else if (mk.kind === 'none') add_('err', 'core.c_no_spawns', _('core.c_no_spawns'));
  else {
    const n = mk.kind === 'ctf' ? mk.counts.ctf : mk.kind === 'team' ? mk.counts.team : dm;
    add_(n >= 8 ? 'ok' : n >= 2 ? 'warn' : 'err', 'core.c_dm_spawns', _('core.c_dm_spawns', { count: n, kind: mk.kind === 'ctf' ? 'CTF' : mk.kind === 'team' ? 'Team' : 'Deathmatch' }) + (n >= 8 ? '' : n >= 2 ? ' ' + _('core.c_dm_better') : ' ' + _('core.c_dm_need')));
    if (!starts.length) add_('info', 'core.c_dm_no_start', _('core.c_dm_no_start'));
    if (mk.counts.monsters > 0 || mk.counts.changelevel > 0) add_('info', 'core.c_dm_monsters', _('core.c_dm_monsters'));
  }
  const lights = doc.ents.filter((e) => /^light(_spot|_environment)?$/.test(e.kv.classname)).length;
  add_(lights ? 'ok' : 'warn', lights ? 'core.c_lights' : 'core.c_no_light', lights ? _('core.c_lights', { count: lights }) : _('core.c_no_light'));
  const solids = solidList(doc), stuck: string[] = [], stuckEnts: Entity[] = [];
  doc.ents.forEach((e) => {
    const org = parseVec(e.kv.origin);
    if (!org || e.brushes.length) return;
    if (!/^(info_player|monster_|weapon_|ammo_|item_|light)/.test(e.kv.classname || '')) return;
    if (pointInSolid(solids, org)) { stuck.push(e.kv.classname); stuckEnts.push(e); }
  });
  if (stuck.length) add_('warn', 'core.c_stuck', _('core.c_stuck', { count: stuck.length, names: stuck.slice(0, 3).join(', ') }) + (stuck.length > 3 ? ' …' : ''), pick(...stuckEnts));
  else if (solids.length) add_('ok', 'core.c_no_stuck', _('core.c_no_stuck'));
  const so = starts[0] && parseVec(starts[0].kv.origin);
  if (so && solids.length && !o.skipLeak) {
    const r = leakTest(solids, so), gt = r.g ? ' ' + _('core.c_grid', { g: r.g }) : '';
    if (r.status === 'sealed') {
      add_('ok', 'core.c_sealed', _('core.c_sealed') + gt);
      const inVoid = r.isVoid ? doc.ents.filter((e, i) => {
        const org = parseVec(e.kv.origin);
        return i > 0 && !!org && !e.brushes.length && /^(info_player|monster_|weapon_|ammo_|item_|light(_spot)?$)/.test(e.kv.classname || '') && r.isVoid!(org);
      }) : [];
      if (inVoid.length) add_('warn', 'core.c_void_ents', _('core.c_void_ents', { count: inVoid.length, names: inVoid.slice(0, 3).map((e) => e.kv.classname).join(', ') }) + (inVoid.length > 3 ? ' …' : ''), selOfEnts(inVoid));
    }
    else if (r.status === 'leak') add_('err', 'core.c_leak', _('core.c_leak') + gt);
    else if (r.status === 'start-solid') add_('err', 'core.c_start_solid', _('core.c_start_solid'), pick(starts[0]));
    else if (r.status === 'outside') add_('err', 'core.c_start_outside', _('core.c_start_outside'), pick(starts[0]));
    else add_('info', 'core.c_leak_skipped', _('core.c_leak_skipped'));
  }
  if (o.hasTex) {
    const hasTex = o.hasTex;
    const missing = Object.keys(texs).filter((t) => !SPECIAL.test(t) && !hasTex(t));
    if (missing.length) {
      const miss = new Set(missing), bs: string[] = [];
      doc.ents.forEach((e) => e.brushes.forEach((b) => { if (b.faces.some((f) => miss.has(f.tex.toUpperCase()))) bs.push(b.id); }));
      add_('warn', 'core.c_tex_missing', _('core.c_tex_missing', { count: missing.length, names: missing.slice(0, 5).join(', ') }) + (missing.length > 5 ? ' …' : ''), { b: bs, e: [] });
    }
    else add_('ok', 'core.c_tex_ok', _('core.c_tex_ok'));
  } else {
    const tn = Object.keys(texs);
    if (tn.length) add_('info', 'core.c_tex_list', _('core.c_tex_list', { count: tn.length, names: tn.slice(0, 6).join(', ') }) + (tn.length > 6 ? ' …' : ''));
  }
  if (world && world.kv.wad) add_('info', 'wad_list', 'WAD: ' + world.kv.wad);
  out.push(...complexityItems(doc));
  const hid = hiddenBrushes(doc);
  out.push(...performanceItems(doc, hid));
  out.push(...lintItems(doc, hid, o.texSize));
  return out;
}

/** Schwerste Stufe unter den Ergebnissen: 'err', 'warn' oder null (nur ok/info); dazu die Zahl je Stufe (für die Markierung des Prüfen-Knopfs) */
export function worstLevel(items: CheckItem[]): { level: 'err' | 'warn' | null; err: number; warn: number } {
  const err = items.filter((i) => i.level === 'err').length, warn = items.filter((i) => i.level === 'warn').length;
  return { level: err ? 'err' : warn ? 'warn' : null, err, warn };
}

/** Hinweise zum Compile-Aufwand (VIS-Zeit); auch vor dem Compile angezeigt */
export function complexityItems(doc: Doc): CheckItem[] {
  return findings(analyze(doc)).map((f) => ({ level: f.level, id: FINDING_KEYS[f.id], text: _(FINDING_KEYS[f.id], f.params), sel: f.sel, fix: f.fix }));
}

/** Map-Typ aus den Spawns: deathmatch (>=2 info_player_deathmatch), ctf, team, sp (Fortschrittsmap mit info_player_start), none. */
export function mapKind(ents?: Entity[]): MapKind {
  const c = { start: 0, dm: 0, team: 0, ctf: 0, monsters: 0, changelevel: 0 };
  (ents || []).forEach((e) => {
    const k = (e.kv && e.kv.classname) || '';
    if (k === 'info_player_start') c.start++; else if (k === 'info_player_deathmatch') c.dm++;
    else if (k === 'info_player_team1' || k === 'info_player_team2') c.team++; else if (k === 'info_ctfspawn') c.ctf++;
    else if (/^monster_/.test(k)) c.monsters++; else if (k === 'trigger_changelevel') c.changelevel++;
  });
  let kind: MapKind['kind'], label: string, why: string;
  if (c.ctf) { kind = 'ctf'; label = _('core.k_ctf'); why = c.ctf + ' info_ctfspawn'; }
  else if (c.team) { kind = 'team'; label = _('core.k_team'); why = c.team + ' info_player_team1/2'; }
  else if (c.dm >= 2 || (c.dm && !c.start)) { kind = 'dm'; label = _('core.k_dm'); why = c.dm + ' info_player_deathmatch'; }
  else if (c.start) { kind = 'sp'; label = _('core.k_sp'); why = 'info_player_start' + (c.dm ? ', ' + _('core.k_sp_only', { count: c.dm }) : ', ' + _('core.k_sp_none')); }
  else { kind = 'none'; label = _('core.k_none'); why = _('core.k_no_spawns'); }
  return { kind: kind, label: label, why: why, counts: c };
}
